import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useContent } from '../context/ContentContext';
import { styled } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PageviewIcon from '@mui/icons-material/Pageview';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link } from 'react-router-dom';

// Styled components
const DashboardCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
  }
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: color || theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const StatsValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}));

function Dashboard() {
  const { content } = useContent();
  
  // In a real app, you would fetch these stats from an API
  const stats = [
    {
      title: 'Total Pages',
      value: '12',
      icon: <PageviewIcon fontSize="large" />,
      color: '#0071e3',
    },
    {
      title: 'Media Files',
      value: '48',
      icon: <EditIcon fontSize="large" />,
      color: '#34c759',
    },
    {
      title: 'Settings',
      value: '8',
      icon: <SettingsIcon fontSize="large" />,
      color: '#ff9500',
    },
  ];

  // Recent activities (mock data)
  const recentActivities = [
    { action: 'Updated home page content', time: '2 hours ago' },
    { action: 'Added new service item', time: '1 day ago' },
    { action: 'Uploaded 3 new project images', time: '2 days ago' },
    { action: 'Modified site settings', time: '5 days ago' },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Dashboard
      </Typography>
      
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <DashboardCard>
              <CardContent sx={{ p: 3 }}>
                <IconWrapper color={stat.color}>
                  {stat.icon}
                </IconWrapper>
                <StatsValue>
                  {stat.value}
                </StatsValue>
                <Typography variant="h6" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </DashboardCard>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={4}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button 
                  component={Link}
                  to="/admin/content"
                  variant="outlined" 
                  fullWidth 
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  Edit Content
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  component={Link}
                  to="/admin/media"
                  variant="outlined" 
                  fullWidth 
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  Manage Media
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  component={Link}
                  to="/admin/settings"
                  variant="outlined" 
                  fullWidth 
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  Site Settings
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  component={Link}
                  to="/"
                  variant="outlined" 
                  fullWidth 
                  sx={{ p: 2, borderRadius: 2 }}
                  color="secondary"
                >
                  View Site
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Recent Activities
            </Typography>
            <List sx={{ width: '100%' }}>
              {recentActivities.map((activity, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={activity.action}
                      secondary={activity.time}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                  {index < recentActivities.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard; 